import { useState } from 'react';
import { Button, Group, Table, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { formatMoney } from '@erp/shared';
import { actsApi, type ActListItem } from '../api/acts';
import { useAuth } from '../auth/AuthContext';
import { RegistryPage } from '../components/RegistryPage';

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('ru-RU');
}

export function ActsPage() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canCreate = hasRole('ADMIN', 'ACCOUNTANT', 'MANAGER');

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);

  const list = useQuery({
    queryKey: ['acts', { search: debouncedSearch }],
    queryFn: () => actsApi.list({ search: debouncedSearch }),
  });

  const items = list.data?.items ?? [];

  return (
    <RegistryPage
      title="Акты выполненных работ"
      action={
        canCreate && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/acts/new')}>
            Новый акт
          </Button>
        )
      }
      description={
        <Text size="sm" c="dimmed">
          Акт можно создать с нуля или на основании выставленного счёта — откройте счёт и нажмите
          «Создать акт».
        </Text>
      }
      filters={
        <Group>
          <TextInput
            placeholder="Поиск по номеру или контрагенту"
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={320}
          />
        </Group>
      }
    >
      <Table striped highlightOnHover verticalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Номер</Table.Th>
            <Table.Th>Дата</Table.Th>
            <Table.Th>Организация</Table.Th>
            <Table.Th>Заказчик</Table.Th>
            <Table.Th>Счёт-основание</Table.Th>
            <Table.Th>Сумма</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((act: ActListItem) => (
            <Table.Tr key={act.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/acts/${act.id}`)}>
              <Table.Td>
                <Text size="sm" fw={500}>
                  № {act.number}
                </Text>
              </Table.Td>
              <Table.Td>{formatDate(act.date)}</Table.Td>
              <Table.Td>{act.organization.shortName}</Table.Td>
              <Table.Td>{act.counterparty.shortName}</Table.Td>
              <Table.Td>{act.invoice ? `№ ${act.invoice.number}` : '—'}</Table.Td>
              <Table.Td>{formatMoney(act.total)}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {!list.isLoading && items.length === 0 && (
        <Text c="dimmed" size="sm" ta="center" py="xl">
          Акты не найдены
        </Text>
      )}
    </RegistryPage>
  );
}
